import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Wallet, PiggyBank, TrendingUp, Receipt, ArrowRight, 
  Plus, Settings, Loader2, AlertCircle, CheckCircle,
  Edit2, Trash2, X, Link2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import GlassPanel from '../../components/GlassPanel';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const financeSubNav = [
  { path: '/finance', label: 'Overview', icon: Wallet },
  { path: '/finance/pots', label: 'Money Pots', icon: PiggyBank },
  { path: '/finance/cashflow', label: 'Cashflow', icon: TrendingUp },
  { path: '/finance/invoices', label: 'Invoices', icon: Receipt },
  { path: '/finance/xero', label: 'Xero', icon: Link2 },
];

const potTypeLabels = {
  OPERATING: { label: 'Operating', description: 'Day-to-day business expenses', color: 'var(--accent-primary)' },
  TAX: { label: 'Tax Reserve', description: 'Reserved for tax payments', color: 'var(--status-warning)' },
  PROFIT: { label: 'Profit', description: 'Business profit allocation', color: 'var(--status-success)' },
  RESERVE: { label: 'Reserve', description: 'Emergency reserve fund', color: 'var(--accent-secondary)' }
};

export function Pots() {
  const location = useLocation();
  const { orgId, getAuthHeaders, canConfigure, isFounder } = useAuth();
  
  const [pots, setPots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPot, setEditingPot] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state for editing
  const [editForm, setEditForm] = useState({
    balance: 0,
    income_floor: 0,
    allocation_percentage: 0,
    description: ''
  });

  const fetchPots = useCallback(async () => {
    if (!orgId) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/finance/${orgId}/pots`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setPots(data);
      } else if (response.status === 401) {
        setError('Please log in to view money pots');
      } else {
        setError('Failed to load money pots');
      }
    } catch (err) {
      console.error('Error fetching pots:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }, [orgId, getAuthHeaders]);

  useEffect(() => {
    fetchPots();
  }, [fetchPots]);

  const handleEditClick = (pot) => {
    if (!canConfigure()) {
      toast.error('Permission denied', { 
        description: 'Only Founders and Owners can configure money pots' 
      });
      return;
    }
    
    setEditingPot(pot);
    setEditForm({
      balance: pot.balance || 0,
      income_floor: pot.income_floor || 0,
      allocation_percentage: pot.allocation_percentage || 0,
      description: pot.description || ''
    });
    setShowConfigModal(true);
  };

  const handleSaveConfig = async () => {
    if (!editingPot) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/finance/${orgId}/pots/${editingPot.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        toast.success('Money pot updated', { description: `${potTypeLabels[editingPot.pot_type]?.label} pot saved successfully` });
        setShowConfigModal(false);
        setEditingPot(null);
        fetchPots();
      } else if (response.status === 403) {
        toast.error('Permission denied', { description: 'You do not have permission to configure money pots' });
      } else {
        const err = await response.json();
        toast.error('Update failed', { description: err.detail || 'Failed to update money pot' });
      }
    } catch (err) {
      console.error('Error saving pot:', err);
      toast.error('Update failed', { description: 'Network error. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const totalBalance = pots.reduce((sum, pot) => sum + (pot.balance || 0), 0);

  return (
    <div className="page-container" data-testid="pots-page">
      {/* Sub Navigation */}
      <div className="sub-nav" style={{ marginBottom: 'var(--space-xl)' }}>
        {financeSubNav.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`btn ${location.pathname === item.path ? 'btn-primary' : 'btn-secondary'}`}
          >
            <item.icon size={16} />
            {item.label}
          </Link>
        ))}
      </div>

      {/* Header */}
      <div className="page-header" style={{ marginBottom: 'var(--space-xl)' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: '600', color: 'var(--text-primary)' }}>
          Money Pots
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
          Manage your business funds with smart allocation
        </p>
      </div>

      {/* Error State */}
      {error && (
        <div style={{
          padding: 'var(--space-md)',
          background: 'var(--status-danger-bg)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-lg)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)'
        }}>
          <AlertCircle size={18} style={{ color: 'var(--status-danger)' }} />
          <span style={{ color: 'var(--status-danger)' }}>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-3xl)' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
        </div>
      )}

      {/* Total Balance */}
      {!loading && !error && (
        <>
          <GlassPanel style={{ marginBottom: 'var(--space-xl)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Total Balance Across All Pots
                </div>
                <div style={{ fontSize: '2rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                  {formatCurrency(totalBalance)}
                </div>
              </div>
              {canConfigure() && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                  <CheckCircle size={14} style={{ color: 'var(--status-success)' }} />
                  <span style={{ fontSize: '0.75rem', color: 'var(--status-success)' }}>
                    {isFounder() ? 'Founder Access' : 'Owner Access'}
                  </span>
                </div>
              )}
            </div>
          </GlassPanel>

          {/* Pots Grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
            gap: 'var(--space-lg)' 
          }}>
            {pots.map((pot) => {
              const typeInfo = potTypeLabels[pot.pot_type] || { label: pot.pot_type, color: 'var(--text-muted)' };
              
              return (
                <GlassPanel key={pot.id} data-testid={`pot-${pot.pot_type.toLowerCase()}`}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--space-md)' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: 'var(--radius-md)',
                      background: `${typeInfo.color}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <PiggyBank size={20} style={{ color: typeInfo.color }} />
                    </div>
                    <button
                      onClick={() => handleEditClick(pot)}
                      className="btn btn-ghost"
                      style={{ padding: 'var(--space-xs)' }}
                      data-testid={`edit-pot-${pot.pot_type.toLowerCase()}`}
                    >
                      <Settings size={16} />
                    </button>
                  </div>
                  
                  <div style={{ marginBottom: 'var(--space-sm)' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--text-primary)' }}>
                      {typeInfo.label}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {pot.description || typeInfo.description}
                    </div>
                  </div>
                  
                  <div style={{ fontSize: '1.5rem', fontWeight: '600', color: typeInfo.color }}>
                    {formatCurrency(pot.balance)}
                  </div>
                  
                  <div style={{ 
                    marginTop: 'var(--space-md)', 
                    paddingTop: 'var(--space-md)', 
                    borderTop: '1px solid var(--glass-border)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    color: 'var(--text-muted)'
                  }}>
                    <span>Allocation: {pot.allocation_percentage || 0}%</span>
                    <span>Floor: {formatCurrency(pot.income_floor)}</span>
                  </div>
                </GlassPanel>
              );
            })}
          </div>

          {/* Empty State */}
          {pots.length === 0 && (
            <GlassPanel>
              <div style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
                <PiggyBank size={48} style={{ color: 'var(--text-muted)', marginBottom: 'var(--space-md)' }} />
                <h3 style={{ color: 'var(--text-primary)', marginBottom: 'var(--space-sm)' }}>No Money Pots</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  Money pots will be initialized automatically.
                </p>
              </div>
            </GlassPanel>
          )}
        </>
      )}

      {/* Configuration Modal */}
      {showConfigModal && editingPot && (
        <div 
          className="modal-overlay" 
          onClick={() => setShowConfigModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 'var(--space-lg)'
          }}
          data-testid="pot-config-modal"
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-secondary)',
              borderRadius: 'var(--radius-xl)',
              border: '1px solid var(--glass-border)',
              padding: 'var(--space-xl)',
              width: '100%',
              maxWidth: '400px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-lg)' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                Configure {potTypeLabels[editingPot.pot_type]?.label} Pot
              </h2>
              <button 
                onClick={() => setShowConfigModal(false)}
                className="btn btn-ghost"
                style={{ padding: 'var(--space-xs)' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
              {/* Balance */}
              <div>
                <label style={{ display: 'block', marginBottom: 'var(--space-xs)', color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: '500' }}>
                  Current Balance (£)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.balance}
                  onChange={(e) => setEditForm({ ...editForm, balance: parseFloat(e.target.value) || 0 })}
                  style={{
                    width: '100%',
                    padding: 'var(--space-md)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '1rem'
                  }}
                  data-testid="pot-balance-input"
                />
              </div>

              {/* Allocation Percentage */}
              <div>
                <label style={{ display: 'block', marginBottom: 'var(--space-xs)', color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: '500' }}>
                  Income Allocation (%)
                </label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={editForm.allocation_percentage}
                  onChange={(e) => setEditForm({ ...editForm, allocation_percentage: parseFloat(e.target.value) || 0 })}
                  style={{
                    width: '100%',
                    padding: 'var(--space-md)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '1rem'
                  }}
                  data-testid="pot-allocation-input"
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 'var(--space-xs)' }}>
                  Percentage of income automatically allocated to this pot
                </p>
              </div>

              {/* Income Floor */}
              <div>
                <label style={{ display: 'block', marginBottom: 'var(--space-xs)', color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: '500' }}>
                  Income Floor (£)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.income_floor}
                  onChange={(e) => setEditForm({ ...editForm, income_floor: parseFloat(e.target.value) || 0 })}
                  style={{
                    width: '100%',
                    padding: 'var(--space-md)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '1rem'
                  }}
                  data-testid="pot-floor-input"
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 'var(--space-xs)' }}>
                  Minimum threshold before allocation kicks in
                </p>
              </div>

              {/* Description */}
              <div>
                <label style={{ display: 'block', marginBottom: 'var(--space-xs)', color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: '500' }}>
                  Description
                </label>
                <input
                  type="text"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Optional description"
                  style={{
                    width: '100%',
                    padding: 'var(--space-md)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '1rem'
                  }}
                  data-testid="pot-description-input"
                />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 'var(--space-md)', marginTop: 'var(--space-md)' }}>
                <button
                  onClick={() => setShowConfigModal(false)}
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveConfig}
                  disabled={saving}
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                  data-testid="pot-save-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Pots;
